import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PenLine, QrCode, Tag, Truck } from 'lucide-react'
import { api } from '@/lib/api'
import { useStorefrontProducts } from '@/lib/useStorefrontCategories'
import type { Tier } from '@/types'
import BrandStrip from '@/components/storefront/BrandStrip'
import CategoryBrowser from '@/components/storefront/CategoryBrowser'
import Cta from '@/components/storefront/Cta'
import HeroCarousel from '@/components/storefront/HeroCarousel'
import Reveal from '@/components/storefront/Reveal'
import Testimonials from '@/components/storefront/Testimonials'

/**
 * One clause each, carrying what the old "How it works" paragraphs said.
 * Bare icons on purpose - the tinted circles they used to sit in were the
 * thing that made this strip look padded out.
 */
const trustPoints = [
  { icon: Tag, text: 'Flat price per piece, no minimum order' },
  { icon: PenLine, text: 'Names and numbers on every size' },
  { icon: QrCode, text: 'Track your order at every stage' },
  { icon: Truck, text: 'Self pickup or courier, date up front' },
]

export default function Home() {
  const products = useStorefrontProducts()
  const tiers = useQuery({
    queryKey: ['storefront-tiers'],
    queryFn: async () => (await api.get<{ data: Tier[] }>('/storefront/tiers')).data.data,
  })

  // Local, not the URL: browsing on the home page should not rewrite history.
  const [category, setCategory] = useState<string | null>(null)

  return (
    <div>
      {/* Admin-managed hero carousel (admin > Hero) */}
      <HeroCarousel />

      {/* Browse by category. Replaces the old Best sellers grid and Shop by
          category chip row, which rendered the same catalog twice. */}
      <CategoryBrowser
        products={products.data}
        tiers={tiers.data}
        isLoading={products.isLoading}
        value={category}
        onChange={setCategory}
        title="Shop our range"
        subtitle="Flat per-piece pricing - no minimum order surprises"
        limit={5}
        showViewAll
      />

      {/* Brand strip, copied from the reference's Shop by Apparel section */}
      <BrandStrip />

      {/* Trust row. Flat hairline cards, not .box - that recipe's offset
          ::before frame is exactly the decoration this section is avoiding. */}
      <section className="mx-auto w-full max-w-6xl px-5 py-10">
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {trustPoints.map(({ icon: Icon, text }, i) => (
            <Reveal key={text} delay={i * 0.06}>
              <div className="flex h-full flex-col items-center gap-3 rounded-xl border border-foreground/10 bg-background p-6 text-center">
                <Icon className="size-6 text-foreground/70" strokeWidth={1.75} />
                <p className="text-sm font-semibold sm:text-base">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Social proof between our own claims and the final ask. */}
      <Testimonials />

      {/* Brand statement, on the houndstooth motif (public/motif.png). */}
      <section className="bg-motif border-t border-foreground/10 bg-[#f4f4f4] bg-repeat">
        {/* Logo pinned at the top, statement centered in the space below it,
            like the reference's tall section. */}
        <Reveal className="mx-auto flex min-h-[70vh] w-full max-w-3xl flex-col items-center px-5 py-14 text-center sm:py-16">
          <img src="/logo_hd.png" alt="" className="h-8 w-auto object-contain" />
          <div className="flex flex-1 flex-col items-center justify-center gap-5 py-16">
            <h2 className="text-3xl font-extrabold uppercase leading-tight tracking-[-0.02em] sm:text-5xl">
              The home of custom team wear
            </h2>
            <p className="max-w-xl text-base text-foreground/60">
              Jerseys, corporate wear and caps with names and numbers on every size - printed on a tracked five-stage
              line
            </p>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
              <Cta to="/register">Get started</Cta>
              <Cta to="/products" variant="white">
                Browse products
              </Cta>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  )
}
